/**
 * get a label to display the time passed between
 * the input time and now
 */
export const timeAgo = (
  time: string,
  noSeconds?: boolean,
  shortTimeDescriptions?: boolean
): string => {
  return getTimeAgoWithDeltaToNext(time, noSeconds, shortTimeDescriptions)
    .timeAgo;
};

const TIMES = {
  MINUTE: 60,
  HOUR: 60 * 60,
  DAY: 60 * 60 * 24,
  YEAR: 60 * 60 * 24 * 365,
};

// this function returns the user friendly time ago string
// and also the time in ms until the next time description
// so we can set timeouts to update the time ago string
// without updating it every second
export const getTimeAgoWithDeltaToNext = (
  time: string,
  noSeconds?: boolean,
  shortTimeDescriptions?: boolean
): { timeAgo: string; deltaToNext: number } => {
  const timeDate = Date.parse(time);
  const now = Date.now();
  const diff = now - timeDate;

  const diffAsSeconds = diff / 1000;

  if (diffAsSeconds < 20) {
    return {
      timeAgo: 'just now',
      deltaToNext: (20 - Math.floor(diffAsSeconds)) * 1000,
    };
  }

  if (diffAsSeconds < 40) {
    return {
      timeAgo: noSeconds
        ? 'just now'
        : `20 ${shortTimeDescriptions ? 's.' : 'seconds'} ago`,
      deltaToNext: (40 - Math.floor(diffAsSeconds)) * 1000,
    };
  }

  if (diffAsSeconds < 60) {
    return {
      timeAgo: noSeconds
        ? 'just now'
        : `40 ${shortTimeDescriptions ? 's.' : 'seconds'} ago`,
      deltaToNext: (60 - Math.floor(diffAsSeconds)) * 1000,
    };
  }

  if (diffAsSeconds < TIMES.HOUR) {
    const minuteDiff = Math.floor(diffAsSeconds / 60);
    return {
      timeAgo: `${minuteDiff} ${
        shortTimeDescriptions ? 'min.' : `minute${minuteDiff > 1 ? 's' : ''}`
      } ago`,
      // we want to re-tick every minute here so we need to calculate the time until the next minute tick would occur
      // if it is exactly a minute, we handle that case separately
      // otherwise calculate the time until the next minute tick
      deltaToNext:
        (diffAsSeconds === 60 ? 60 : (60 - (diffAsSeconds % 60)) % 60) * 1000,
    };
  }

  if (diffAsSeconds < TIMES.DAY) {
    const hourDiff = Math.floor(diffAsSeconds / TIMES.HOUR);
    return {
      timeAgo: `${hourDiff} hour${hourDiff > 1 ? 's' : ''} ago`,
      // we want to re-tick every hour here so we need to calculate the time until the next hour tick would occur
      // if it is exactly an hour, we handle that case separately
      // otherwise calculate the time until the next hour tick
      deltaToNext:
        (diffAsSeconds === TIMES.HOUR
          ? TIMES.HOUR
          : TIMES.HOUR - (diffAsSeconds % TIMES.HOUR)) * 1000,
    };
  }

  if (diffAsSeconds < TIMES.YEAR) {
    const dayDiff = Math.floor(diffAsSeconds / TIMES.DAY);
    return {
      timeAgo: `${dayDiff} day${dayDiff > 1 ? 's' : ''} ago`,
      // we want to re-tick every day here so we need to calculate the time until the next day tick would occur
      // if it is exactly a day, we handle that case separately
      // otherwise calculate the time until the next day tick
      deltaToNext:
        (diffAsSeconds === TIMES.DAY
          ? TIMES.DAY
          : TIMES.DAY - (diffAsSeconds % TIMES.DAY)) * 1000,
    };
  }

  const yearDiff = Math.floor(diffAsSeconds / TIMES.YEAR);
  return {
    timeAgo: `${yearDiff} year${yearDiff > 1 ? 's' : ''} ago`,
    // we want to re-tick every year here so we need to calculate the time until the next year tick would occur
    deltaToNext:
      (diffAsSeconds === TIMES.YEAR
        ? TIMES.YEAR
        : TIMES.YEAR - (diffAsSeconds % TIMES.YEAR)) * 1000,
  };
};
